// src/automation/components/AutomationConfigTab.js
//
// Admin tab: register a new automation (sidebar entry) and manage each task's
// raw config JSON (download / replace). Admin-only.
//
// Note: registering an automation makes it appear in the sidebar and lets you
// store its config, but RUNNING it still needs the task's handler code deployed
// (a tasks/<name>.py module). The UI flags this clearly.

import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Download, Upload, RefreshCw, Eye, EyeOff, Trash2, RotateCcw } from "lucide-react";
import {
  getAutomationGroups,
  getTaskConfig,
  saveTaskConfig,
  saveRegistryEntry,
  clearRunHistory,
  deleteAutomation,
  getDeletedAutomations,
  restoreAutomation,
  migrateSchedules,
} from "../api/automation_data";
import { CollapsibleSection, FormField, Toast } from "wbr/components/admin/AdminShared";
import ScheduleEditor, { cleanSchedules, defaultSchedules } from "./ScheduleEditor";

function download(filename, text) {
  const blob = new Blob([text], { type: "application/json;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Starter config for a newly registered automation, shaped like the live
// configs so it opens cleanly in the editor. Queue Status Reports (and any other
// section) get an EMAIL trigger with blank recipients; Rapid gets a SLACK trigger.
export function starterConfig({ name, label, section, section_order, order, schedules }) {
  const base = {
    name,
    label,
    section,
    section_order,
    order,
    enabled: false,
    hidden: false,
    start_date: new Date().toISOString().slice(0, 10),
    minutes_saved_per_run: 0,
    description: "",
    maps: [],
    extraction: {
      days_back: 30,
      client_ids: [178, 321],
      states: ["Created", "Missed", "Reserved"],
      batch_size: 50,
    },
  };
  // The schedule lives on the trigger (there is no task-level schedule).
  const sched = schedules || defaultSchedules();
  if (String(section || "").toLowerCase().includes("rapid")) {
    return {
      ...base,
      triggers: [{
        id: "default",
        slack: { channel_id: "", only_on_changes: false, report_every_cycle: true },
        schedules: sched,
      }],
    };
  }
  return {
    ...base,
    assets: { queue_file: "", login_map: "", persona_map: "" },
    triggers: [{
      id: "default",
      filter: "",
      email: {
        from_address: "no-reply@pulse-cia.amazon.dev",
        display_only: false,
        mode: "flat",
        subject: label,
        to: "",
        cc: "",
        bcc: "",
      },
      schedules: sched,
    }],
  };
}

// One-off: move every task's old task-level schedule onto its triggers. Preview
// first; Apply saves each task (logged + revertible in the Activity Log).
function ScheduleMigration({ onToast, onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const go = async (dryRun) => {
    if (!dryRun && !window.confirm("Move every task's schedule onto its triggers now? Each task is saved and logged (revertible).")) return;
    setBusy(true);
    try {
      const res = await migrateSchedules(dryRun);
      setResult(res);
      onToast({ message: res?.summary || "Done", type: res?.success ? "success" : "error" });
      if (!dryRun) onDone && onDone();
    } catch (e) {
      onToast({ message: e.message || "Failed", type: "error" });
    } finally {
      setBusy(false);
    }
  };
  const rows = (result?.tasks || []).filter((r) => r.status !== "ok" || !result.dry_run);
  return (
    <CollapsibleSection title="Trigger schedules" subtitle="Schedules now live on each trigger — move old task-level schedules">
      <p className="admin-field__hint" style={{ marginTop: 0 }}>
        Each trigger has its own schedule (nothing is scheduled at task level). Tasks saved before this
        still carry a task-level schedule; it is moved onto their triggers the next time they're saved,
        or all at once here. Same timing — only where it's stored changes.
      </p>
      <div style={{ display: "flex", gap: 8 }}>
        <button className="admin-btn admin-btn--sm admin-btn--outline" disabled={busy} onClick={() => go(true)}>
          <Eye size={14} /> Preview
        </button>
        <button className="admin-btn admin-btn--sm admin-btn--primary" disabled={busy} onClick={() => go(false)}>
          <RefreshCw size={14} /> {busy ? "Working…" : "Move all schedules to triggers"}
        </button>
      </div>
      {result && (
        <div style={{ marginTop: 10 }}>
          <p className="admin-field__hint">{result.summary}</p>
          {rows.length > 0 && (
            <ul className="admin-field__hint" style={{ margin: 0, paddingLeft: 18 }}>
              {rows.map((r) => (
                <li key={r.task}><code>{r.task}</code> — {r.status.replace("_", " ")}: {r.detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </CollapsibleSection>
  );
}

function ConfigRow({ taskName, label, hidden, custom = false, canEdit = false, canDelete = false, onToast, onChanged }) {
  const [busy, setBusy] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm(
      `Delete "${label}"?\n\nThis removes it from the sidebar, deletes its config and disables its schedule. ` +
      "A backup of the config is kept in S3 (archive/)."
    )) return;
    setBusy(true);
    try {
      const res = await deleteAutomation(taskName);
      onToast({ message: res?.summary || `Deleted ${label}`, type: "success" });
      onChanged && onChanged();
    } catch (e) {
      onToast({ message: e.message || "Delete failed", type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const handleToggleHidden = async () => {
    setBusy(true);
    try {
      const cfg = await getTaskConfig(taskName);
      if (!cfg) {
        onToast({ message: `No config found for ${taskName}`, type: "error" });
        return;
      }
      await saveTaskConfig(taskName, { ...cfg, hidden: !hidden });
      onToast({
        message: `${label} is now ${!hidden ? "hidden" : "visible"}`,
        type: "success",
      });
      onChanged && onChanged();
    } catch (e) {
      onToast({ message: e.message || "Failed to update visibility", type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      const cfg = await getTaskConfig(taskName);
      if (!cfg) {
        onToast({ message: `No config found for ${taskName}`, type: "error" });
        return;
      }
      download(`${taskName}.json`, JSON.stringify(cfg, null, 2));
    } finally {
      setBusy(false);
    }
  };

  const handleReplace = async (fileObj) => {
    setBusy(true);
    try {
      const text = await fileObj.text();
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch {
        onToast({ message: "Invalid JSON file", type: "error" });
        return;
      }
      if (parsed.name && parsed.name !== taskName) {
        onToast({ message: `config.name "${parsed.name}" must match ${taskName}`, type: "error" });
        return;
      }
      parsed.name = taskName;
      await saveTaskConfig(taskName, parsed);
      onToast({ message: `Config replaced for ${taskName}`, type: "success" });
    } catch (e) {
      onToast({ message: e.message || "Replace failed", type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-access-user__row">
      <div className="admin-access-user__fields">
        <span style={{ fontWeight: 600, opacity: hidden ? 0.55 : 1 }}>{label}</span>
        <code className="auto-main-code">{taskName}</code>
        {hidden && <span className="admin-role-badge admin-role-badge--viewer">Hidden</span>}
        {!custom && <span className="admin-role-badge admin-role-badge--viewer" title="Has task code in the Lambda">Built-in</span>}
        {!canEdit && <span className="admin-role-badge admin-role-badge--viewer" title="You don't have edit rights on this task">View only</span>}
      </div>
      <div className="admin-access-user__actions">
        {canDelete && (
          <button
            className="admin-btn admin-btn--sm admin-btn--outline"
            onClick={handleDelete}
            disabled={busy}
            title="Delete this automation and its config (restorable below)"
            style={{ color: "#c9302c", borderColor: "#c9302c" }}
          >
            <Trash2 size={12} /> Delete
          </button>
        )}
        {canEdit && (
          <button
            className="admin-btn admin-btn--sm admin-btn--outline"
            onClick={handleToggleHidden}
            disabled={busy}
            title={hidden ? "Show in sidebar" : "Hide from sidebar"}
          >
            {hidden ? <EyeOff size={12} /> : <Eye size={12} />} {hidden ? "Hidden" : "Visible"}
          </button>
        )}
        <button className="admin-btn admin-btn--sm admin-btn--outline" onClick={handleDownload} disabled={busy}>
          {busy ? <RefreshCw size={12} /> : <Download size={12} />} Download
        </button>
        {canEdit && (
          <label className="admin-btn admin-btn--sm admin-btn--outline" style={{ cursor: "pointer" }}>
            <Upload size={12} /> Replace
            <input
              type="file"
              accept=".json,application/json"
              style={{ display: "none" }}
              disabled={busy}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleReplace(f);
                e.target.value = "";
              }}
            />
          </label>
        )}
      </div>
    </div>
  );
}

// Deleted automations (admin): list + Restore.
function DeletedAutomations({ onToast, onChanged }) {
  const { data = [], isLoading, refetch } = useQuery({
    queryKey: ["deletedAutomations"],
    queryFn: getDeletedAutomations,
    staleTime: 30 * 1000,
  });
  const [busy, setBusy] = useState(null);
  const restore = async (name) => {
    setBusy(name);
    try {
      const res = await restoreAutomation(name);
      onToast({ message: res?.summary || `Restored ${name}`, type: "success" });
      await refetch();
      onChanged && onChanged();
    } catch (e) {
      onToast({ message: e.message || "Restore failed", type: "error" });
    } finally {
      setBusy(null);
    }
  };
  if (isLoading) return <p className="admin-field__hint">Loading…</p>;
  if (!data.length) return <div className="admin-empty-state admin-empty-state--sm"><p>Nothing deleted.</p></div>;
  return data.map((d) => (
    <div className="admin-access-user__row" key={d.name}>
      <div className="admin-access-user__fields">
        <code className="auto-main-code">{d.name}</code>
        <span className="admin-field__hint">
          deleted {d.deleted_at ? new Date(d.deleted_at).toLocaleString() : ""} {d.deleted_by ? `by ${d.deleted_by}` : ""}
        </span>
        {d.builtin && <span className="admin-role-badge admin-role-badge--viewer">Built-in</span>}
      </div>
      <div className="admin-access-user__actions">
        <button className="admin-btn admin-btn--sm admin-btn--outline" disabled={busy === d.name} onClick={() => restore(d.name)}>
          <RotateCcw size={12} /> {busy === d.name ? "Restoring…" : "Restore"}
        </button>
      </div>
    </div>
  ));
}

export default function AutomationConfigTab({ perms = {} }) {
  const qc = useQueryClient();
  const isAdmin = !!perms.isAdmin;
  const canEditTask = perms.canEditTask || (() => false);
  const [toast, setToast] = useState(null);
  const blankForm = () => ({
    name: "", label: "", section: "Queue Status Reports", section_order: 1, order: 99,
    schedules: defaultSchedules(),
  });
  const [form, setForm] = useState(blankForm);
  const [adding, setAdding] = useState(false);

  const { data: sections = [] } = useQuery({
    queryKey: ["automationGroups"],
    queryFn: getAutomationGroups,
    staleTime: 60 * 1000,
  });

  const tasks = [];
  for (const s of sections) for (const t of s.tasks || []) tasks.push({ name: t.name, label: t.label, hidden: !!t.hidden, custom: !!t.custom });

  const refreshLists = () => {
    qc.invalidateQueries({ queryKey: ["automationGroups"] });
    qc.invalidateQueries({ queryKey: ["automationBootstrap"] });
    qc.invalidateQueries({ queryKey: ["deletedAutomations"] });
  };

  const [resetting, setResetting] = useState(false);
  const handleResetMetrics = async () => {
    if (
      !window.confirm(
        "Reset ALL dashboard metrics?\n\nThis wipes Total runs, Time saved, and the full run history so you can start testing from zero. This cannot be undone."
      )
    ) {
      return;
    }
    setResetting(true);
    try {
      const res = await clearRunHistory(null);
      setToast({ message: res?.summary || "Dashboard metrics reset", type: "success" });
      qc.invalidateQueries({ queryKey: ["automationRunSummary"] });
      qc.invalidateQueries({ queryKey: ["automationRuns"] });
      qc.invalidateQueries({ queryKey: ["automationBootstrap"] });
    } catch (e) {
      setToast({ message: e.message || "Failed to reset metrics", type: "error" });
    } finally {
      setResetting(false);
    }
  };

  const handleAdd = async () => {
    if (!form.name.trim()) {
      setToast({ message: "Name is required", type: "error" });
      return;
    }
    setAdding(true);
    try {
      await saveRegistryEntry({
        name: form.name.trim(),
        label: form.label.trim() || form.name.trim(),
        section: form.section,
        section_order: Number(form.section_order) || 99,
        order: Number(form.order) || 99,
      });
      // Seed a section-appropriate starter config (email trigger with blank
      // recipients for Queue Status Reports) so it opens ready to fill in.
      await saveTaskConfig(form.name.trim(), starterConfig({
        name: form.name.trim(),
        label: form.label.trim() || form.name.trim(),
        section: form.section,
        section_order: Number(form.section_order) || 99,
        order: Number(form.order) || 99,
        schedules: cleanSchedules(form.schedules),
      }));
      setToast({ message: `Registered ${form.name}. Deploy its handler code to run it.`, type: "success" });
      setForm(blankForm());
      refreshLists();
      // A contributor is auto-granted the new automation — refresh their rights.
      qc.invalidateQueries({ queryKey: ["autoPermissions"] });
    } catch (e) {
      setToast({ message: e.message || "Failed to register", type: "error" });
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="admin-tab-content">
      <CollapsibleSection title="Add Automation" subtitle="Register a new sidebar entry (handler code deployed separately)">
        <div className="admin-field-row" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 12 }}>
          <FormField label="Name (id)" required hint="lowercase_with_underscores">
            <input className="admin-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. kyc_new_mail" />
          </FormField>
          <FormField label="Label">
            <input className="admin-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="KYC New" />
          </FormField>
          <FormField label="Section">
            <input className="admin-input" value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} />
          </FormField>
          <FormField label="Section order">
            <input className="admin-input" type="number" value={form.section_order} onChange={(e) => setForm({ ...form, section_order: e.target.value })} />
          </FormField>
          <FormField label="Order">
            <input className="admin-input" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
          </FormField>
        </div>
        <div style={{ margin: "12px 0" }}>
          <ScheduleEditor value={form.schedules} onChange={(s) => setForm({ ...form, schedules: s })} />
          <p className="admin-field__hint">
            This becomes the schedule of the automation's first trigger. Add more triggers (e.g. a Slack one
            with a different schedule) on its page.
          </p>
          <p className="admin-field__hint">
            New automations start disabled — switch <code>enabled</code> on in its General block when it's ready.
          </p>
        </div>
        <button className="admin-btn admin-btn--primary" onClick={handleAdd} disabled={adding}>
          <Plus size={14} /> {adding ? "Adding…" : "Add Automation"}
        </button>
        <p className="admin-field__hint">
          This creates the sidebar entry + a starter config in S3. To actually run it,
          deploy a <code>tasks/&lt;name&gt;.py</code> handler and redeploy the Lambda.
        </p>
      </CollapsibleSection>

      {isAdmin && <ScheduleMigration onToast={setToast} onDone={refreshLists} />}

      {isAdmin && (
      <CollapsibleSection title="Dashboard Metrics" subtitle="Reset the cover-page KPIs (Total runs / Time saved) and run history">
        <p className="admin-field__hint" style={{ marginTop: 0 }}>
          Clears the stored run history and per-task summary so the cover-page
          KPIs (Total runs, Time saved) and the Recent Runs table all go back to
          zero. Use this to start a clean test run, then deploy. This does not
          change any task config or schedule.
        </p>
        <button
          className="admin-btn admin-btn--sm admin-btn--outline"
          onClick={handleResetMetrics}
          disabled={resetting}
          title="Wipe Total runs / Time saved / run history"
        >
          {resetting ? <RefreshCw size={12} className="auto-eb-spin" /> : <Trash2 size={12} />}{" "}
          {resetting ? "Resetting…" : "Reset dashboard metrics"}
        </button>
      </CollapsibleSection>
      )}

      {isAdmin && (
        <CollapsibleSection title="Deleted automations" subtitle="Restore something deleted from the sidebar" defaultOpen={false}>
          <DeletedAutomations onToast={setToast} onChanged={refreshLists} />
        </CollapsibleSection>
      )}

      <CollapsibleSection title="Config Files" subtitle="Download or replace each automation's raw config JSON">
        {tasks.length === 0 ? (
          <div className="admin-empty-state admin-empty-state--sm"><p>No automations.</p></div>
        ) : (
          tasks.map((t) => (
            <ConfigRow
              key={t.name}
              taskName={t.name}
              label={t.label}
              hidden={t.hidden}
              custom={t.custom}
              canEdit={canEditTask(t.name)}
              canDelete={isAdmin}
              onToast={setToast}
              onChanged={refreshLists}
            />
          ))
        )}
      </CollapsibleSection>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
